import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Calendar,
  Plus,
  Settings,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';

interface MobileNavItemProps {
  icon: React.ElementType;
  label: string;
  to: string;
}

const MobileNavItem = ({ icon: Icon, label, to }: MobileNavItemProps) => {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');

  return (
    <Link
      to={to}
      className={cn(
        'flex flex-col items-center justify-center gap-1 flex-1 min-w-0 py-2 px-1',
        'transition-colors duration-200',
        'active:bg-accent/50', // Touch feedback
        isActive
          ? 'text-primary font-medium'
          : 'text-muted-foreground hover:text-foreground'
      )}
    >
      <Icon className={cn('h-5 w-5 shrink-0', isActive && 'text-primary')} />
      <span className="text-xs truncate w-full text-center">{label}</span>
    </Link>
  );
};

export const MobileNav = () => {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'ADMIN';

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-t border-border">
      <div className="flex items-center justify-around max-w-screen-sm mx-auto">
        <MobileNavItem icon={LayoutDashboard} label="Dashboard" to="/dashboard" />
        <MobileNavItem icon={Calendar} label="Historia" to="/history" />

        {/* Central FAB-style button for new meeting */}
        <Link
          to="/new-meeting"
          className={cn(
            'flex flex-col items-center justify-center gap-1',
            'flex-1 min-w-0 py-2 px-1',
            'transition-all duration-200',
            'active:scale-95' // Touch feedback
          )}
        >
          <div className="flex items-center justify-center h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg active:shadow-md transition-shadow">
            <Plus className="h-6 w-6" />
          </div>
          <span className="text-xs text-muted-foreground">Nowe</span>
        </Link>

        {isAdmin && <MobileNavItem icon={Settings} label="Admin" to="/admin" />}
        {!isAdmin && (
          <div className="flex-1" /> /* Spacer for balance */
        )}
      </div>

      {/* Safe area for devices with notch/home indicator */}
      <div className="h-safe-area-inset-bottom" />
    </nav>
  );
};
